import React, {
  type ComponentProps,
  useCallback,
  useRef,
  useState,
} from 'react';
import {
  FlatList,
  LayoutChangeEvent,
  type ListRenderItem,
  StyleSheet,
  View,
} from 'react-native';
import { theme, useUniqueNativeID, webOnlyStyles } from '@sg/garnish';

import {
  ProductDetailsCarouselNav,
  ProductDetailsCarouselSlide,
} from './components';
import { type ProductDetailsCardCarouselItem } from './ProductDetailsCarousel.types';
import { useSharedValue } from 'react-native-reanimated';

// ─────────────────────────────────────────────────────────────────────────────

export const ProductDetailsCarousel = (props: Props) => {
  const { items, onActiveSlideChange } = props;

  const uniqueId = useUniqueNativeID('product-details-carousel');

  const lastIndex = items.length - 1;

  // ─── Shared Values ───────────────────────────────────────────────────

  const containerWidthSV = useSharedValue(0);
  const containerHeightSV = useSharedValue(0);

  // ─── Refs ────────────────────────────────────────────────────────────

  const flatListRef = useRef<FlatList>(null);

  // ─── State ───────────────────────────────────────────────────────────

  const [activeIndex, setActiveIndex] = useState(0);

  // ─── Helpers ─────────────────────────────────────────────────────────

  // istanbul ignore next
  const onScrollToIndexFailed = useCallback<OnScrollToIndexFailed>((params) => {
    const { index } = params;

    requestIdleCallback(() => {
      flatListRef.current?.scrollToIndex({ index, animated: true });
    });
  }, []);

  const keyExtractor = useCallback<KeyExtractor>(
    (_, index) => `${uniqueId}-item-${index}`,
    [uniqueId],
  );

  // ─────────────────────────────────────────────────────────────────────

  const renderItem: ListRenderItem<ProductDetailsCardCarouselItem> =
    useCallback(
      (params) => {
        const { item, index } = params;

        return (
          <ProductDetailsCarouselSlide
            item={item}
            isActive={index === activeIndex}
            widthSV={containerWidthSV}
            heightSV={containerHeightSV}
          />
        );
      },
      [activeIndex, containerWidthSV, containerHeightSV],
    );

  // istanbul ignore next
  const handleContainerLayout = useCallback(
    (event: LayoutChangeEvent) => {
      const { width, height } = event.nativeEvent.layout;

      containerWidthSV.value = width;
      containerHeightSV.value = height;
    },
    [containerHeightSV, containerWidthSV],
  );

  const handleGetItemLayout = useCallback(
    (_data: unknown, index: number) => ({
      index,
      length: containerWidthSV.value,
      offset: containerWidthSV.value * index,
    }),
    [containerWidthSV],
  );

  // ─── Navigation ─────────────────────────────────────────────────────

  // istanbul ignore next
  const goToPrevSlide = useCallback(() => {
    const prevIndex = activeIndex - 1;

    if (prevIndex < 0) {
      flatListRef.current?.scrollToIndex({
        index: lastIndex,
        animated: true,
      });

      return;
    }

    if (prevIndex >= 0) {
      flatListRef.current?.scrollToIndex({ index: prevIndex, animated: true });
    }
  }, [activeIndex, lastIndex]);

  // istanbul ignore next
  const goToNextSlide = useCallback(() => {
    const nextIndex = activeIndex + 1;

    if (nextIndex > lastIndex) {
      flatListRef.current?.scrollToIndex({ index: 0, animated: true });

      return;
    }

    flatListRef.current?.scrollToIndex({ index: nextIndex, animated: true });
  }, [activeIndex, lastIndex]);

  const handleScroll = useCallback<NonNullable<FlatListProps['onScroll']>>(
    (event) => {
      const { contentOffset } = event.nativeEvent;

      const isInteger = Number.isInteger(contentOffset.x / containerWidthSV.value); // prettier-ignore

      if (!isInteger) return;

      const activeIndex = contentOffset.x / containerWidthSV.value;

      setActiveIndex(activeIndex);
      onActiveSlideChange?.(activeIndex);
    },
    [containerWidthSV, onActiveSlideChange],
  );

  // ─── Flags ───────────────────────────────────────────────────────────

  const hasMultipleItems = items.length > 1;
  const hasPrev = hasMultipleItems && activeIndex > 0;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.container} onLayout={handleContainerLayout}>
      <>
        <FlatList
          ref={flatListRef}
          data={items}
          renderItem={renderItem}
          keyExtractor={keyExtractor}
          horizontal
          pagingEnabled
          scrollEnabled={false}
          showsHorizontalScrollIndicator={false}
          decelerationRate="fast"
          getItemLayout={handleGetItemLayout}
          onScroll={handleScroll}
          onScrollToIndexFailed={onScrollToIndexFailed}
          scrollEventThrottle={16}
          windowSize={2}
          removeClippedSubviews={false}
          maxToRenderPerBatch={2}
          updateCellsBatchingPeriod={50}
          initialNumToRender={2}
          style={flatListWebStyles}
        />

        {hasMultipleItems ? (
          <ProductDetailsCarouselNav
            hasPrev={hasPrev}
            onPrevPress={goToPrevSlide}
            onNextPress={goToNextSlide}
          />
        ) : null}
      </>
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    width: '100%',
    height: '100%',
  },
});

const flatListWebStyles = webOnlyStyles({
  outlineColor: theme.colors.GRAY,
  outlineOffset: theme.spacing['1'],
  outlineWidth: 2,
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  items: ProductDetailsCardCarouselItem[];
  onActiveSlideChange?: (activeSlideIndex: number) => void;
};

type OnScrollToIndexFailed = NonNullable<FlatListProps['onScrollToIndexFailed']>; // prettier-ignore
type KeyExtractor = NonNullable<FlatListProps['keyExtractor']>;
type FlatListProps = ComponentProps<typeof FlatList>;
